import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Link2,
  Plus,
  Globe,
  Key,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Copy,
  RefreshCw,
  Activity,
  Server,
  Zap,
  Bot,
  BarChart3,
  Clock,
  Trash2,
  ExternalLink,
  Filter,
  ChevronRight,
  Settings,
  Users,
  Webhook,
  Eye,
  EyeOff,
  ArrowDownToLine,
  PlugZap,
  Info,
} from "lucide-react";
import APIKeysManager from "./APIKeysManager";
import ReverseSyncPanel from "../components/ReverseSyncPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SuperAdminConnect = ({ getAdminHeaders }) => {
  const [activeTab, setActiveTab] = useState("platforms");
  const [loading, setLoading] = useState(true);
  const [platformCode, setPlatformCode] = useState("");
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [connectionLogs, setConnectionLogs] = useState([]);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({
    platform_url: "",
    platform_code: "",
    api_key: "",
  });
  const [connecting, setConnecting] = useState(false);
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [connectionStep, setConnectionStep] = useState(1);

  // OkGo Integration state
  const [okgoConfig, setOkgoConfig] = useState(null);
  const [okgoLoading, setOkgoLoading] = useState(false);
  const [okgoSaving, setOkgoSaving] = useState(false);
  const [okgoTesting, setOkgoTesting] = useState(false);
  const [okgoSyncing, setOkgoSyncing] = useState(false);
  const [okgoTestResult, setOkgoTestResult] = useState(null);
  const [okgoSyncResult, setOkgoSyncResult] = useState(null);
  const [okgoSyncLog, setOkgoSyncLog] = useState([]);
  const [oneTimeWebhookSecret, setOneTimeWebhookSecret] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [okgoForm, setOkgoForm] = useState({
    api_key: "",
    base_url: "https://app.okgo.mx",
    default_pin: "1234",
    enabled: true,
  });

  useEffect(() => {
    fetchPlatformCode();
    fetchConnectedPlatforms();
    fetchConnectionStatus();
  }, []);

  useEffect(() => {
    if (activeTab === "okgo") {
      fetchOkgoConfig();
      fetchOkgoSyncLog();
    }
  }, [activeTab]);

  // ==================== OkGo Handlers ====================

  const fetchOkgoConfig = async () => {
    setOkgoLoading(true);
    try {
      const res = await fetch(`${API}/admin/integrations/okgo`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setOkgoConfig(data);
        setOkgoForm({
          api_key: "",
          base_url: data.base_url || "https://app.okgo.mx",
          default_pin: data.default_pin || "1234",
          enabled: data.enabled !== false,
        });
      }
    } catch (e) {
      console.error("Error fetching OkGo config:", e);
    } finally {
      setOkgoLoading(false);
    }
  };

  const fetchOkgoSyncLog = async () => {
    try {
      const res = await fetch(`${API}/admin/integrations/okgo/sync-log`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (res.ok) setOkgoSyncLog(await res.json());
    } catch (e) {
      console.error("Error fetching OkGo sync log:", e);
    }
  };

  const handleSaveOkgo = async () => {
    setOkgoSaving(true);
    try {
      const payload = { ...okgoForm };
      if (!payload.api_key) delete payload.api_key;
      const res = await fetch(`${API}/admin/integrations/okgo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAdminHeaders() },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success("Configuración de OkGo guardada");
        fetchOkgoConfig();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Error al guardar");
      }
    } catch (e) {
      toast.error("Error de conexión");
    } finally {
      setOkgoSaving(false);
    }
  };

  const handleTestOkgo = async () => {
    setOkgoTesting(true);
    setOkgoTestResult(null);
    try {
      const res = await fetch(`${API}/admin/integrations/okgo/test`, {
        method: "POST",
        headers: getAdminHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        setOkgoTestResult({ ok: true, message: data.message, users: data.users_total });
        toast.success(data.message);
      } else {
        setOkgoTestResult({ ok: false, message: data.detail });
        toast.error(data.detail || "Error al probar conexión");
      }
    } catch (e) {
      setOkgoTestResult({ ok: false, message: "Error de red" });
      toast.error("Error de red");
    } finally {
      setOkgoTesting(false);
    }
  };

  const handleSyncOkgo = async () => {
    setOkgoSyncing(true);
    setOkgoSyncResult(null);
    try {
      const res = await fetch(`${API}/admin/integrations/okgo/sync`, {
        method: "POST",
        headers: getAdminHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      if (res.ok) {
        setOkgoSyncResult(data);
        toast.success(data.message);
        fetchOkgoConfig();
        fetchOkgoSyncLog();
      } else {
        toast.error(data.detail || "Error al sincronizar");
      }
    } catch (e) {
      toast.error("Error de red");
    } finally {
      setOkgoSyncing(false);
    }
  };

  const handleRegenerateSecret = async () => {
    try {
      const res = await fetch(`${API}/admin/integrations/okgo/regenerate-secret`, {
        method: "POST",
        headers: getAdminHeaders(),
        credentials: "include",
      });
      if (res.ok) {
        const data = await res.json();
        setOneTimeWebhookSecret(data.webhook_secret || "");
        setOkgoConfig((prev) => ({
          ...prev,
          webhook_secret_configured: true,
          webhook_secret_source: "secure_admin_config",
        }));
        toast.success(data.message || "Secreto generado; cópialo ahora");
      } else {
        const data = await res.json();
        toast.error(data.detail || "Error al regenerar secreto");
      }
    } catch (e) {
      toast.error("Error al regenerar secreto");
    }
  };

  const getWebhookUrl = () => {
    const base = window.location.origin;
    return `${base}/api/webhooks/okgo/register`;
  };

  const fetchPlatformCode = async () => {
    try {
      const response = await fetch(`${API}/admin/platform-code`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setPlatformCode(data.code);
      }
    } catch (error) {
      console.error("Error fetching platform code:", error);
    }
  };

  const fetchConnectedPlatforms = async () => {
    try {
      const response = await fetch(`${API}/admin/connect/platforms`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setConnectedPlatforms(data);
      }
    } catch (error) {
      console.error("Error fetching platforms:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const response = await fetch(`${API}/admin/connect/status`, {
        credentials: "include",
        headers: getAdminHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data);
        setConnectionLogs(data.recent_logs || []);
      }
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  const handleConnect = async () => {
    if (!connectForm.platform_url || !connectForm.platform_code || !connectForm.api_key) {
      toast.error("Completa todos los campos");
      return;
    }

    setConnecting(true);
    try {
      const response = await fetch(`${API}/admin/connect/platform`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAdminHeaders(),
        },
        credentials: "include",
        body: JSON.stringify(connectForm),
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableEndpoints(data.available_endpoints || []);
        setSelectedEndpoints(data.available_endpoints?.map((e) => e.path) || []);
        setConnectionStep(2);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al conectar");
      }
    } catch (error) {
      toast.error("Error de conexión");
    } finally {
      setConnecting(false);
    }
  };

  const handleConfirmConnection = async () => {
    // The platform is already connected, just update selected endpoints
    const latestPlatform = connectedPlatforms[connectedPlatforms.length - 1];
    if (latestPlatform) {
      try {
        await fetch(`${API}/admin/connect/platforms/${latestPlatform.connection_id}/endpoints`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...getAdminHeaders(),
          },
          credentials: "include",
          body: JSON.stringify(selectedEndpoints),
        });
      } catch (error) {
        console.error("Error updating endpoints:", error);
      }
    }

    toast.success("Plataforma conectada exitosamente");
    setShowConnectModal(false);
    setConnectionStep(1);
    setConnectForm({ platform_url: "", platform_code: "", api_key: "" });
    fetchConnectedPlatforms();
    fetchConnectionStatus();
  };

  const handleDisconnect = async (connectionId) => {
    try {
      const response = await fetch(`${API}/admin/connect/platforms/${connectionId}`, {
        method: "DELETE",
        credentials: "include",
        headers: getAdminHeaders(),
      });

      if (response.ok) {
        toast.success("Plataforma desconectada");
        fetchConnectedPlatforms();
        fetchConnectionStatus();
      }
    } catch (error) {
      toast.error("Error al desconectar");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case "error":
        return <XCircle className="w-4 h-4 text-red-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
    }
  };

  return (
    <div>
      {/* Header with Platform Code */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Super Admin Connect
          </h1>
          <p className="text-slate-400 mt-1">Conecta OkChat con otras plataformas</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Platform Code Badge */}
          <div className="bg-gradient-to-r from-red-600/20 to-purple-600/20 border border-red-600/30 rounded-lg px-4 py-2">
            <p className="text-xs text-slate-400 mb-1">Código de Plataforma</p>
            <div className="flex items-center gap-2">
              <code className="text-lg font-mono text-white font-bold">{platformCode || "---"}</code>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-slate-400 hover:text-white"
                onClick={() => copyToClipboard(platformCode)}
              >
                <Copy className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <Button onClick={() => setShowConnectModal(true)} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            Vincular Plataforma
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border border-slate-700">
          <TabsTrigger value="platforms" className="data-[state=active]:bg-red-600">
            <Link2 className="w-4 h-4 mr-2" />
            Plataformas Conectadas
          </TabsTrigger>
          <TabsTrigger value="agent" className="data-[state=active]:bg-red-600">
            <Bot className="w-4 h-4 mr-2" />
            Agente IA de Conexión
          </TabsTrigger>
          <TabsTrigger value="status" className="data-[state=active]:bg-red-600">
            <Activity className="w-4 h-4 mr-2" />
            Panel de Estado
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="data-[state=active]:bg-red-600">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="okgo" className="data-[state=active]:bg-red-600">
            <PlugZap className="w-4 h-4 mr-2" />
            OkGo
          </TabsTrigger>
          <TabsTrigger value="reverse-sync" className="data-[state=active]:bg-red-600">
            <RefreshCw className="w-4 h-4 mr-2" />
            Sincronización
          </TabsTrigger>
        </TabsList>

        {/* Reverse Sync Tab */}
        <TabsContent value="reverse-sync" className="mt-6">
          <ReverseSyncPanel getAdminHeaders={getAdminHeaders} />
        </TabsContent>

        {/* Platforms Tab */}
        <TabsContent value="platforms" className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : connectedPlatforms.length === 0 ? (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-12 text-center">
                <Link2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">No hay plataformas conectadas</h3>
                <p className="text-slate-400 mb-4">
                  Conecta otras plataformas para sincronizar datos y funcionalidades
                </p>
                <Button onClick={() => setShowConnectModal(true)} className="bg-red-600 hover:bg-red-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Conectar primera plataforma
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {connectedPlatforms.map((platform) => (
                <Card key={platform.connection_id} className="bg-slate-800 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-red-600 to-purple-600 rounded-lg flex items-center justify-center">
                          <Globe className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-white font-medium">{platform.platform_url}</h4>
                            {getStatusIcon(platform.status)}
                          </div>
                          <p className="text-sm text-slate-400">
                            Código: <code className="text-red-400">{platform.platform_code}</code>
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                              {platform.selected_endpoints?.length || 0} endpoints activos
                            </Badge>
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                platform.oauth_sso_status === "active"
                                  ? "border-green-600 text-green-400"
                                  : "border-yellow-600 text-yellow-400"
                              }`}
                            >
                              OAuth: {platform.oauth_sso_status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => handleDisconnect(platform.connection_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* AI Agent Tab */}
        <TabsContent value="agent" className="mt-6">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Bot className="w-5 h-5 text-red-500" />
                Agente IA de Conexión
              </CardTitle>
              <CardDescription className="text-slate-400">
                El agente automatiza la conexión y vinculación con otras plataformas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {[
                  {
                    icon: Zap,
                    title: "Detección automática",
                    desc: "Detecta endpoints y webhooks disponibles",
                  },
                  {
                    icon: Shield,
                    title: "Validación OAuth",
                    desc: "Prueba automática de conexiones SSO",
                  },
                  {
                    icon: RefreshCw,
                    title: "Sincronización",
                    desc: "Mantiene las conexiones actualizadas",
                  },
                ].map((feature, idx) => (
                  <div key={idx} className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                    <feature.icon className="w-8 h-8 text-red-500 mb-3" />
                    <h4 className="text-white font-medium">{feature.title}</h4>
                    <p className="text-sm text-slate-400 mt-1">{feature.desc}</p>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-gradient-to-r from-red-600/10 to-purple-600/10 border border-red-600/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <Bot className="w-6 h-6 text-red-400 mt-1" />
                  <div>
                    <p className="text-white font-medium">Estado del Agente</p>
                    <p className="text-sm text-slate-400 mt-1">
                      El agente se activa automáticamente al vincular una nueva plataforma. Analiza la API remota,
                      detecta endpoints compatibles y configura webhooks necesarios.
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                      <Badge className="bg-green-600 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Activo
                      </Badge>
                      <span className="text-xs text-slate-500">
                        Última actividad: {new Date().toLocaleString("es-MX")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Status Tab */}
        <TabsContent value="status" className="mt-6">
          {/* Stats Grid */}
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            {[
              {
                label: "Plataformas totales",
                value: connectionStatus?.total_platforms || 0,
                icon: Server,
                color: "text-blue-400",
              },
              {
                label: "Activas",
                value: connectionStatus?.active_platforms || 0,
                icon: CheckCircle,
                color: "text-green-400",
              },
              {
                label: "Inactivas",
                value: connectionStatus?.inactive_platforms || 0,
                icon: XCircle,
                color: "text-red-400",
              },
              {
                label: "Incidencias recientes",
                value: connectionStatus?.recent_incidents || 0,
                icon: AlertCircle,
                color: "text-yellow-400",
              },
            ].map((stat, idx) => (
              <Card key={idx} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm">{stat.label}</p>
                      <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                    </div>
                    <stat.icon className={`w-8 h-8 ${stat.color} opacity-50`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Logs */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">Registros de Conexión</CardTitle>
                <Button variant="outline" size="sm" className="border-slate-600 text-slate-300">
                  <Filter className="w-4 h-4 mr-2" />
                  Filtrar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {connectionLogs.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No hay registros recientes</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {connectionLogs.map((log, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        {log.status === "success" ? (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        ) : log.status === "error" ? (
                          <XCircle className="w-4 h-4 text-red-400" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-yellow-400" />
                        )}
                        <div>
                          <p className="text-white text-sm">{log.event}</p>
                          <p className="text-xs text-slate-500">{log.platform_code}</p>
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(log.timestamp).toLocaleString("es-MX")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="api-keys" className="mt-6">
          <APIKeysManager getAdminHeaders={getAdminHeaders} />
        </TabsContent>

        {/* OkGo Integration Tab */}
        <TabsContent value="okgo" className="mt-6">
          {okgoLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-orange-600/10 to-red-600/10 border border-orange-600/20 rounded-xl">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <PlugZap className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-bold text-white">Integración OkGo</h2>
                    {okgoConfig?.enabled && okgoConfig?.api_key ? (
                      <Badge className="bg-green-600 text-white text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" /> Activa
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-600 text-slate-300 text-xs">
                        <AlertCircle className="w-3 h-3 mr-1" /> Sin configurar
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-400 text-sm mt-1">
                    Conecta OkChat con la plataforma OkGo (Bubble.io). Los usuarios registrados en OkGo
                    se crean automáticamente en OkChat con su nickname y PIN genérico.
                  </p>
                </div>
                {okgoConfig?.last_sync && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-slate-500">Último sync</p>
                    <p className="text-xs text-slate-300">
                      {new Date(okgoConfig.last_sync).toLocaleString("es-MX")}
                    </p>
                    <p className="text-xs text-orange-400 mt-1">{okgoConfig.users_synced || 0} usuarios</p>
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Left: API Configuration */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Key className="w-4 h-4 text-orange-400" />
                      Credenciales de API
                    </CardTitle>
                    <CardDescription className="text-slate-400 text-xs">
                      Configura la conexión con la API de OkGo (Bubble.io)
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-slate-300 text-sm">URL Base de OkGo</Label>
                      <Input
                        value={okgoForm.base_url}
                        onChange={(e) => setOkgoForm({ ...okgoForm, base_url: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white mt-1 font-mono text-sm"
                        placeholder="https://app.okgo.mx"
                      />
                      <p className="text-xs text-slate-500 mt-1">URL principal del servidor OkGo</p>
                    </div>

                    <div>
                      <Label className="text-slate-300 text-sm">API Key de OkGo</Label>
                      <div className="flex gap-2 mt-1">
                        <div className="relative flex-1">
                          <Input
                            type={showApiKey ? "text" : "password"}
                            value={okgoForm.api_key}
                            onChange={(e) => setOkgoForm({ ...okgoForm, api_key: e.target.value })}
                            className="bg-slate-900 border-slate-700 text-white pr-10 font-mono text-sm"
                            placeholder={okgoConfig?.api_key_masked || "Ingresa el API Key de OkGo"}
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                            onClick={() => setShowApiKey(!showApiKey)}
                          >
                            {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      {okgoConfig?.api_key_masked && (
                        <p className="text-xs text-slate-500 mt-1">
                          Clave actual: <code className="text-orange-400">{okgoConfig.api_key_masked}</code>
                          {" "}— Deja vacío para conservarla
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-slate-300 text-sm">PIN por defecto</Label>
                      <Input
                        value={okgoForm.default_pin}
                        onChange={(e) => setOkgoForm({ ...okgoForm, default_pin: e.target.value })}
                        className="bg-slate-900 border-slate-700 text-white mt-1 font-mono text-sm"
                        placeholder="1234"
                        maxLength={8}
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        PIN numérico que se asigna a los nuevos usuarios de OkGo al crearse en OkChat.
                        El usuario puede cambiarlo después.
                      </p>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        onClick={handleSaveOkgo}
                        disabled={okgoSaving}
                        className="bg-orange-600 hover:bg-orange-700 flex-1"
                      >
                        {okgoSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                        Guardar configuración
                      </Button>
                      <Button
                        onClick={handleTestOkgo}
                        disabled={okgoTesting || !okgoConfig?.api_key}
                        variant="outline"
                        className="border-orange-600 text-orange-400 hover:bg-orange-600/10"
                      >
                        {okgoTesting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4" />
                        )}
                      </Button>
                    </div>

                    {okgoTestResult && (
                      <div className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${
                        okgoTestResult.ok
                          ? "bg-green-600/10 border-green-600/30 text-green-400"
                          : "bg-red-600/10 border-red-600/30 text-red-400"
                      }`}>
                        {okgoTestResult.ok ? (
                          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                          <p>{okgoTestResult.message}</p>
                          {okgoTestResult.users !== undefined && (
                            <p className="text-xs mt-1 text-green-300">
                              {okgoTestResult.users} usuarios encontrados en OkGo
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Right: Webhook Config */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white text-base flex items-center gap-2">
                      <Webhook className="w-4 h-4 text-orange-400" />
                      Webhook de Registro
                    </CardTitle>
                    <CardDescription className="text-slate-400 text-xs">
                      Configura este webhook en OkGo para que los nuevos usuarios se creen automáticamente en OkChat
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-blue-600/10 border border-blue-600/20 rounded-lg">
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-300">
                          En OkGo (Bubble.io), ve a <strong>Settings → API → Workflows</strong> y crea un
                          workflow que llame al endpoint de abajo cuando un usuario se registre.
                        </p>
                      </div>
                    </div>

                    <div>
                      <Label className="text-slate-300 text-sm">URL del Webhook</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={getWebhookUrl()}
                          readOnly
                          className="bg-slate-900 border-slate-700 text-orange-300 font-mono text-xs"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="border-slate-600 text-slate-400 hover:text-white flex-shrink-0"
                          onClick={() => { navigator.clipboard.writeText(getWebhookUrl()); toast.success("URL copiada"); }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Método: POST | Encabezado requerido: X-Webhook-Secret</p>
                    </div>

                    <div>
                      <Label className="text-slate-300 text-sm">Secreto del Webhook</Label>
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={oneTimeWebhookSecret || (okgoConfig?.webhook_secret_configured ? "•••••••••••••••••••••••• (configurado)" : "")}
                          readOnly
                          className="bg-slate-900 border-slate-700 text-white font-mono text-xs"
                          type="password"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          className="border-slate-600 text-slate-400 hover:text-white flex-shrink-0"
                          disabled={!oneTimeWebhookSecret}
                          title={oneTimeWebhookSecret ? "Copiar secreto generado" : "Genera un secreto para copiarlo una sola vez"}
                          onClick={() => {
                            navigator.clipboard.writeText(oneTimeWebhookSecret);
                            toast.success("Secreto copiado");
                            setOneTimeWebhookSecret("");
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="border-red-600/40 text-red-400 hover:text-red-300 flex-shrink-0"
                          title="Regenerar secreto"
                          onClick={handleRegenerateSecret}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Usa <code>X-Webhook-Secret</code>. Por compatibilidad también se aceptan Authorization Bearer, X-Okgo-Secret y X-Secret. El valor solo se muestra al generarlo.
                      </p>
                    </div>

                    <div className="border border-slate-700 rounded-lg p-3 bg-slate-900">
                      <p className="text-xs text-slate-400 mb-2 font-medium">Payload esperado del webhook:</p>
                      <pre className="text-xs text-green-300 whitespace-pre-wrap">{JSON.stringify({
                        nickname: "usuario_okgo",
                        name: "Nombre Completo",
                        email: "usuario@ejemplo.com",
                        category: "personal"
                      }, null, 2)}</pre>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sync Section */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-white text-base flex items-center gap-2">
                        <ArrowDownToLine className="w-4 h-4 text-orange-400" />
                        Sincronización Manual de Usuarios
                      </CardTitle>
                      <CardDescription className="text-slate-400 text-xs mt-1">
                        Importa todos los usuarios existentes de OkGo a OkChat en un solo paso
                      </CardDescription>
                    </div>
                    <Button
                      onClick={handleSyncOkgo}
                      disabled={okgoSyncing || !okgoConfig?.api_key}
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      {okgoSyncing ? (
                        <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sincronizando...</>
                      ) : (
                        <><RefreshCw className="w-4 h-4 mr-2" /> Sincronizar ahora</>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {okgoSyncResult && (
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        { label: "Creados", value: okgoSyncResult.created, color: "text-green-400", bg: "bg-green-600/10 border-green-600/20" },
                        { label: "Actualizados", value: okgoSyncResult.updated, color: "text-blue-400", bg: "bg-blue-600/10 border-blue-600/20" },
                        { label: "Errores", value: okgoSyncResult.errors, color: "text-red-400", bg: "bg-red-600/10 border-red-600/20" },
                      ].map((s) => (
                        <div key={s.label} className={`p-3 rounded-lg border ${s.bg} text-center`}>
                          <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                          <p className="text-xs text-slate-400 mt-1">{s.label}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Sync Log */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {okgoSyncLog.length === 0 ? (
                      <p className="text-slate-500 text-sm text-center py-4">Sin registros de sincronización aún</p>
                    ) : (
                      okgoSyncLog.map((log, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-2 bg-slate-900 rounded border border-slate-700 text-sm"
                        >
                          <div className="flex items-center gap-2">
                            {log.status === "success" ? (
                              <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                            )}
                            <span className="text-slate-300 text-xs">{log.message}</span>
                          </div>
                          <span className="text-xs text-slate-500 flex-shrink-0 ml-2">
                            {new Date(log.timestamp).toLocaleString("es-MX")}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* How it Works */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white text-sm flex items-center gap-2">
                    <Users className="w-4 h-4 text-orange-400" />
                    Flujo de integración OkGo → OkChat
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-3">
                    {[
                      { step: "1", title: "Usuario se registra en OkGo", desc: "El usuario completa su registro en app.okgo.mx", color: "bg-orange-600" },
                      { step: "2", title: "OkGo envía el webhook", desc: "Bubble.io llama automáticamente al endpoint de OkChat con los datos del usuario", color: "bg-yellow-600" },
                      { step: "3", title: "OkChat crea el usuario", desc: "Se crea el usuario con el nickname de OkGo y el PIN numérico genérico configurado", color: "bg-blue-600" },
                      { step: "4", title: "Usuario ingresa a OkChat", desc: "El usuario puede entrar con su nickname de OkGo y su PIN (puede cambiarlo)", color: "bg-green-600" },
                    ].map((s) => (
                      <div key={s.step} className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                        <div className={`w-7 h-7 ${s.color} rounded-full flex items-center justify-center text-white text-sm font-bold mb-2`}>
                          {s.step}
                        </div>
                        <p className="text-white text-sm font-medium">{s.title}</p>
                        <p className="text-slate-400 text-xs mt-1">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Connect Modal */}
      <Dialog open={showConnectModal} onOpenChange={setShowConnectModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-red-500" />
              Vincular Plataforma
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {connectionStep === 1 && "Ingresa las credenciales de la plataforma a conectar"}
              {connectionStep === 2 && "Selecciona los endpoints que deseas sincronizar"}
              {connectionStep === 3 && "Confirma la conexión"}
            </DialogDescription>
          </DialogHeader>

          {connectionStep === 1 && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-slate-300">URL de la plataforma</Label>
                <Input
                  value={connectForm.platform_url}
                  onChange={(e) => setConnectForm({ ...connectForm, platform_url: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                  placeholder="https://app.ejemplo.com"
                />
              </div>
              <div>
                <Label className="text-slate-300">Código único de plataforma</Label>
                <Input
                  value={connectForm.platform_code}
                  onChange={(e) => setConnectForm({ ...connectForm, platform_code: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white mt-1 font-mono"
                  placeholder="OK01041234567"
                />
                <p className="text-xs text-slate-500 mt-1">Formato: OK + 4 dígitos fecha + 7 dígitos</p>
              </div>
              <div>
                <Label className="text-slate-300">API Key</Label>
                <Input
                  type="password"
                  value={connectForm.api_key}
                  onChange={(e) => setConnectForm({ ...connectForm, api_key: e.target.value })}
                  className="bg-slate-900 border-slate-700 text-white mt-1"
                  placeholder="sk-..."
                />
              </div>
            </div>
          )}

          {connectionStep === 2 && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-400">Endpoints disponibles</p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-400"
                  onClick={() =>
                    setSelectedEndpoints(
                      selectedEndpoints.length === availableEndpoints.length
                        ? []
                        : availableEndpoints.map((e) => e.path)
                    )
                  }
                >
                  {selectedEndpoints.length === availableEndpoints.length ? "Deseleccionar todos" : "Seleccionar todos"}
                </Button>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableEndpoints.map((endpoint, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-slate-900 rounded-lg border border-slate-700"
                  >
                    <Checkbox
                      checked={selectedEndpoints.includes(endpoint.path)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedEndpoints([...selectedEndpoints, endpoint.path]);
                        } else {
                          setSelectedEndpoints(selectedEndpoints.filter((p) => p !== endpoint.path));
                        }
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                          {endpoint.method}
                        </Badge>
                        <code className="text-sm text-white">{endpoint.path}</code>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{endpoint.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-green-600/10 border border-green-600/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">OAuth SSO: Disponible</span>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {connectionStep === 1 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowConnectModal(false)}
                  className="border-slate-600 text-slate-300"
                >
                  Cancelar
                </Button>
                <Button onClick={handleConnect} disabled={connecting} className="bg-red-600 hover:bg-red-700">
                  {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                  Conectar
                </Button>
              </>
            )}
            {connectionStep === 2 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setConnectionStep(1)}
                  className="border-slate-600 text-slate-300"
                >
                  Atrás
                </Button>
                <Button onClick={handleConfirmConnection} className="bg-red-600 hover:bg-red-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Confirmar Conexión
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminConnect;
